import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LogOut,
  ListChecks,
  KeyRound,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
} from "lucide-react";
import {
  listResults,
  createResult,
  updateResult,
  deleteResult,
  changePassword,
  apiError,
} from "../../lib/adminApi";
import { useAdminAuth } from "../../context/AdminAuth";

const GAME = "Bhutan Morning Teer";

function toDisplay(sort) {
  const [y, m, d] = sort.split("-");
  return `${d}-${m}-${y}`;
}
function todaySort() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function ResultManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateSort, setDateSort] = useState(todaySort());
  const [fr, setFr] = useState("");
  const [sr, setSr] = useState("");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await listResults());
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditId(null);
    setDateSort(todaySort());
    setFr("");
    setSr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!fr.trim() || !sr.trim()) {
      toast.error("Please enter both F/R and S/R results");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await updateResult(editId, {
          date: toDisplay(dateSort),
          date_sort: dateSort,
          fr: fr.trim(),
          sr: sr.trim(),
        });
        toast.success("Result updated");
      } else {
        await createResult({
          city: GAME,
          date: toDisplay(dateSort),
          date_sort: dateSort,
          fr: fr.trim(),
          sr: sr.trim(),
        });
        toast.success("Result added — now live on Homepage & Previous Results");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setDateSort(r.date_sort);
    setFr(r.fr);
    setSr(r.sr);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete result for ${r.date}?`)) return;
    try {
      await deleteResult(r.id);
      toast.success("Result deleted");
      if (editId === r.id) resetForm();
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      {/* Form */}
      <form
        onSubmit={submit}
        data-testid="result-form"
        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      >
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-[#10161f]">
          {editId ? <Pencil size={18} /> : <Plus size={18} />}
          {editId ? "Edit Result" : "Add Result"}
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">
              Date
            </label>
            <input
              data-testid="result-date"
              type="date"
              value={dateSort}
              onChange={(e) => setDateSort(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#1ab5a3]"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">
              F/R (10:30 AM)
            </label>
            <input
              data-testid="result-fr"
              value={fr}
              onChange={(e) => setFr(e.target.value)}
              type="text"
              maxLength={20}
              placeholder="e.g. 05 or XX"
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#1ab5a3]"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">
              S/R (11:30 AM)
            </label>
            <input
              data-testid="result-sr"
              value={sr}
              onChange={(e) => setSr(e.target.value)}
              type="text"
              maxLength={20}
              placeholder="e.g. 77 or XX"
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#1ab5a3]"
            />
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Tip: keep leading zeroes as they are (e.g. write 05, not 5).
        </p>
        <div className="mt-4 flex gap-3">
          <button
            data-testid="result-save"
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-[#1ab5a3] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0f9b8a] disabled:opacity-60"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            {editId ? "Update Result" : "Add Result"}
          </button>
          {editId && (
            <button
              type="button"
              onClick={resetForm}
              data-testid="result-cancel"
              className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              <X size={15} /> Cancel
            </button>
          )}
        </div>
      </form>

      {/* List */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-bold text-[#10161f]">
          Manage Existing Results
        </h2>
        {loading ? (
          <p className="py-6 text-center text-sm text-slate-500">Loading…</p>
        ) : items.length === 0 ? (
          <p
            data-testid="admin-no-results"
            className="py-6 text-center text-sm text-slate-500"
          >
            No results added yet. Add today's result above.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="admin-results-table" className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="py-2 pr-2 font-semibold">Date</th>
                  <th className="py-2 pr-2 font-semibold">F/R</th>
                  <th className="py-2 pr-2 font-semibold">S/R</th>
                  <th className="py-2 pr-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100">
                    <td className="py-2.5 pr-2 font-medium text-[#10161f]">{r.date}</td>
                    <td className="py-2.5 pr-2 font-bold">{r.fr}</td>
                    <td className="py-2.5 pr-2 font-bold">{r.sr}</td>
                    <td className="py-2.5 pr-2">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => startEdit(r)}
                          data-testid={`edit-${r.date_sort}`}
                          className="flex items-center gap-1 rounded-md bg-sky-50 px-2.5 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100"
                        >
                          <Pencil size={13} /> Edit
                        </button>
                        <button
                          onClick={() => remove(r)}
                          data-testid={`delete-${r.date_sort}`}
                          className="flex items-center gap-1 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100"
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ChangePasswordSection() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirm password do not match");
      return;
    }
    setSaving(true);
    try {
      await changePassword(cur, next);
      toast.success("Password changed successfully");
      setCur("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const field = (label, val, set, testid) => (
    <div>
      <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
      <input
        data-testid={testid}
        type="password"
        value={val}
        onChange={(e) => set(e.target.value)}
        required
        className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#1ab5a3]"
      />
    </div>
  );

  return (
    <form
      onSubmit={submit}
      data-testid="change-password-form"
      className="max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="mb-4 text-lg font-bold text-[#10161f]">Change Password</h2>
      <div className="space-y-4">
        {field("Current Password", cur, setCur, "current-password")}
        {field("New Password", next, setNext, "new-password")}
        {field("Confirm New Password", confirm, setConfirm, "confirm-password")}
      </div>
      <button
        data-testid="change-password-submit"
        type="submit"
        disabled={saving}
        className="mt-5 flex items-center gap-2 rounded-lg bg-[#1ab5a3] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0f9b8a] disabled:opacity-60"
      >
        {saving && <Loader2 size={16} className="animate-spin" />}
        Update Password
      </button>
      <p className="mt-3 text-xs text-slate-500">
        Minimum 8 characters. Your new password is stored securely (hashed).
      </p>
    </form>
  );
}

export default function AdminDashboard() {
  const { username, logout } = useAdminAuth();
  const [tab, setTab] = useState("results");

  const tabBtn = (key, label, Icon) => (
    <button
      onClick={() => setTab(key)}
      data-testid={`tab-${key}`}
      className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
        tab === key
          ? "bg-[#1ab5a3] text-white"
          : "bg-white text-slate-600 hover:bg-slate-100"
      }`}
    >
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-[#0c1622] text-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-lg font-bold">Admin Panel</h1>
            <p className="text-xs text-slate-400">Bhutan Morning Teer • {username}</p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold hover:bg-white/20"
          >
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-5">
        <div className="mb-5 flex gap-3">
          {tabBtn("results", "Result Management", ListChecks)}
          {tabBtn("password", "Change Password", KeyRound)}
        </div>
        {tab === "results" ? <ResultManagement /> : <ChangePasswordSection />}
      </div>
    </div>
  );
}
